<template>
  <div :class="readerClasses">
    <v-touch @tap="closeSidebar"> <div class="t-mask"></div> </v-touch>

    <header class="t-header" role="banner">
      <div class="t-header-inner">
        <a
          v-if="settings.detailLink ?? pubInfo.detailUrl"
          :href="settings.detailLink ?? pubInfo.detailUrl"
          class="t-title"
        >
          {{ pubInfo.title }}
        </a>
        <span v-else class="t-title">{{ pubInfo.title }}</span>
      </div>
    </header>

    <nav
      role="navigation"
      class="t-sidebar"
      ref="sidebar"
      :style="{'--unzoom-sidebar': sidebarUnzoom}"
    >
      <Toolbar direction="vertical" ref="toolbar">
        <ButtonLink
          v-if="settings.homeLink"
          :href="settings.homeLink"
          :icon="
            settings.homeIcon === 'bookshelf' ? 'icon-catalog' : 'icon-home'
          "
          size="large"
          spin="icon-only"
        >
          Document Home
        </ButtonLink>

        <ButtonPush
          v-if="prefs.showZoomButtons || pagingMode === 'tower'"
          icon="icon-larger"
          size="large"
          spin="icon-only"
          @click="zoomIn"
        >
          Zoom in
        </ButtonPush>

        <ButtonPush
          v-if="prefs.showZoomButtons || pagingMode === 'tower'"
          icon="icon-smaller"
          size="large"
          spin="icon-only"
          @click="zoomOut"
        >
          Zoom out
        </ButtonPush>

        <ButtonPush
          @click="toggleSidebar('search')"
          :aria-controls="$id('sidebar-search')"
          :aria-expanded="sidebarOpen('search') ? 'true' : 'false'"
          aria-haspopup="true"
          icon="icon-search"
          size="large"
          spin="icon-only"
        >
          Search
        </ButtonPush>
        <Panel
          class="t-sidebar-search"
          closer="searchToggle"
          :id="$id('sidebar-search')"
          role="region"
          v-show="sidebarOpen('search')"
          @close="closeSidebar"
        >
          <div class="t-bounds">
            <div class="t-panel-section">
              <div class="t-panel-heading">
                <a v-if="settings.backToSearch" :href="settings.backToSearch">
                  Return to search in progress
                </a>
                <a
                  v-else-if="settings.advancedSearchLink"
                  :href="settings.advancedSearchLink"
                >
                  Use advanced search
                </a>
                <div v-else>Search</div>
              </div>
            </div>
            <SearchPanel />
          </div>
        </Panel>

        <ButtonPush
          @click="toggleSidebar('toc')"
          :aria-controls="$id('sidebar-toc')"
          :aria-expanded="sidebarOpen('toc') ? 'true' : 'false'"
          aria-haspopup="true"
          icon="icon-toc"
          size="large"
          spin="icon-only"
        >
          Table of Contents
        </ButtonPush>
        <Panel
          class="t-sidebar-toc"
          closer="tocToggle"
          :id="$id('sidebar-toc')"
          role="region"
          v-show="sidebarOpen('toc')"
          @close="closeSidebar"
        >
          <div class="t-bounds">
            <div v-if="pubInfo.detailUrl" class="t-panel-section">
              <div class="t-panel-heading">
                <a
                  v-if="settings.detailLink ?? pubInfo.detailUrl"
                  :href="settings.detailLink ?? pubInfo.detailUrl"
                >
                  {{ pubInfo.title }}
                </a>
                <span v-else>{{ pubInfo.title }}</span>
              </div>
            </div>
            <div class="t-panel-subheading">Table of Contents</div>
            <Toc :model="contents" />
          </div>
        </Panel>

        <ButtonPush
          @click="toggleSidebar('attachments')"
          :aria-controls="$id('sidebar-attachments')"
          :aria-expanded="sidebarOpen('attachments') ? 'true' : 'false'"
          aria-haspopup="true"
          :icon="
            settings.attachmentsIcon === 'arrow'
              ? 'icon-link-external'
              : 'icon-attach'
          "
          size="large"
          spin="icon-only"
        >
          Attachments
        </ButtonPush>
        <Panel
          class="t-sidebar-attachments"
          closer="attachmentsToggle"
          :id="$id('sidebar-attachments')"
          role="region"
          v-show="sidebarOpen('attachments')"
          @close="closeSidebar"
        >
          <AttachmentsPanel
            :visible="sidebarOpen('attachments')"
            v-bind="{page, pubInfo}"
          />
        </Panel>

        <ButtonPush
          @click="toggleSidebar('settings')"
          :aria-controls="$id('sidebar-settings')"
          :aria-expanded="sidebarOpen('settings') ? 'true' : 'false'"
          aria-haspopup="true"
          icon="icon-settings"
          size="large"
          spin="icon-only"
        >
          Preferences
        </ButtonPush>
        <Panel
          class="t-sidebar-settings"
          closer="settingsToggle"
          :id="$id('sidebar-settings')"
          role="region"
          v-show="sidebarOpen('settings')"
          @close="closeSidebar"
        >
          <div class="t-bounds">
            <div class="t-panel-section">
              <div class="t-panel-subheading">Preferences</div>
              <p v-if="pagingMode === 'strip'">
                <ButtonToggle
                  v-model="prefs.showZoomButtons"
                  size="large"
                  spin="switch"
                >
                  Show Zoom Buttons
                </ButtonToggle>
              </p>
              <p v-if="pagingMode === 'strip' && isMobile">
                <ButtonToggle
                  v-model="prefs.tapToChangePages"
                  size="large"
                  spin="switch"
                >
                  Tap To Change Pages
                </ButtonToggle>
              </p>
              <p>
                <IconLink
                  class="t-view-link t-bold-link"
                  icon="icon-arrows-ccw"
                  size="medium"
                  @click="prefs.reset"
                >
                  Restore Defaults
                </IconLink>
              </p>
            </div>
            <div class="t-panel-section" v-if="settings.feedbackSurvey">
              <div class="t-panel-subheading">Feedback</div>
              <p>
                Help us improve your reading experience.
                <a :href="feedbackLink" target="_blank">
                  Take our 3-minute survey.
                </a>
              </p>
            </div>
          </div>
        </Panel>
      </Toolbar>
    </nav>

    <main class="t-main" role="main">
      <div class="t-viewer">
        <Strip
          ref="viewer"
          v-if="pagingMode === 'strip'"
          v-bind="{
            pageNum,
            pages,
            searchTerms,
            viewerHeight,
            viewerWidth,
          }"
        />
        <Tower
          ref="viewer"
          v-if="pagingMode === 'tower'"
          v-bind="{
            firstPage,
            lastPage,
            pageNum,
            pages,
          }"
        />
        <template v-if="pagingMode === 'strip' && prefs.tapToChangePages">
          <v-touch
            aria-hidden="true"
            class="t-prev-tap-target"
            @tap="tapPrevious"
          ></v-touch>
          <v-touch
            aria-hidden="true"
            class="t-next-tap-target"
            @tap="tapNext"
          ></v-touch>
        </template>
      </div>
    </main>

    <Footer
      :canPageNext="pagingMode === 'strip' && pageNum < lastPage"
      :canPagePrevious="pagingMode === 'strip' && pageNum > firstPage"
      :lastLogicalPage="logicalPages[lastPage - 1]"
      :logicalPage="logicalPages[pageNum - 1]"
    />
  </div>
</template>

<style scoped lang="scss">
.t-prev-tap-target,
.t-next-tap-target {
  position: absolute;
  top: 0;
  width: 10%;
  height: 100%;
}

.t-prev-tap-target {
  left: 0;
}

.t-next-tap-target {
  right: 0;
}
</style>

<script>
import * as R from 'ramda'
import config from '../config'
import api from '../services/api'
import prefs from '../services/prefs'
import AttachmentsPanel from './AttachmentsPanel.vue'
import ButtonLink from './ButtonLink.vue'
import ButtonPush from './ButtonPush.vue'
import ButtonToggle from './ButtonToggle.vue'
import Footer from './Footer.vue'
import IconLink from './IconLink.vue'
import Panel from './Panel.vue'
import SearchPanel from './SearchPanel.vue'
import Strip from './Strip.vue'
import Toc from './Toc.vue'
import Toolbar from './Toolbar.vue'
import Tower from './Tower.vue'

const emptyArr = Object.freeze([])

export default {
  name: 'Reader',

  props: {
    pubInfo: {
      type: Object,
      required: true,
    },
    pagingMode: {
      type: String,
      required: true,
    },
    logicalPages: {
      type: Array,
      required: true,
    },
    pageNum: {
      type: Number,
      required: true,
    },
    setPageNum: {
      type: Function,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
  },

  data() {
    const {firstPage, lastPage, tizraId, urlId} = this.pubInfo

    // Build the full array of page objects, so that accessing
    // pages[pageNum - 1] is always defined, and array operations such
    // as slice and filter don't need special measures.
    const pages = Array.from({length: this.pubInfo.numPages}, (v, i) => ({
      fetched: false,
      isFetching: false,
      pageNum: firstPage + i,
      stripOffset: i,
    }))

    return {
      contents: emptyArr,
      firstPage,
      lastPage,
      pages,
      prefs,
      readMode: false,
      sidebar: null,
      sidebarUnzoom: 1,
      tizraId,
      urlId,
    }
  },

  computed: {
    feedbackLink() {
      let url = new URL(
        'https://docs.google.com/forms/d/e/1FAIpQLSdAO2Vh30onyda8vUC1L8-Duh_fzeUAdx5flnZfoVsmv3shkA/viewform',
      )
      url.searchParams.set('entry.488846905', document.location.hostname)
      return url
    },

    readerClasses() {
      return {
        't-toc-loading': this.contents === emptyArr,
        't-sidebar-open': !!this.sidebar,
        't-sidebar-closed': !this.sidebar,
        't-sidebar-on': !this.readMode,
        't-readmode': this.readMode,
      }
    },

    page() {
      return this.pages[this.pageNum - 1]
    },

    searchTerms() {
      return this.$route.query.highlightText || ''
    },

    viewerHeight() {
      let {height} = this
      if (!this.readMode) {
        height -= 72 // equal to $headerHeight + $footerHeight
      }
      return height
    },

    viewerWidth() {
      let {width} = this
      if (!this.readMode) {
        width -= 40 // equal to $sidebarWidth
      }
      return width
    },
  },

  watch: {
    page() {
      // When either the pages array or current pageNum changes, then
      // we need to check if the page hit an access denied.
      if (this.page.denied) {
        this.$pub('denied', this.page.denied)
      }
    },

    pageNum() {
      this.loadPages()
    },

    $route(newRoute, oldRoute) {
      // Don't close panels etc. just when clearing highlightText.
      const equalRoutes = (a, b) =>
        a.path === b.path &&
        a.hash === b.hash &&
        R.equals(a.params, b.params) &&
        R.equals(a.query, b.query)
      const path = ['query', 'highlightText']
      if (
        R.path(path, oldRoute) &&
        !R.path(path, newRoute) &&
        equalRoutes(R.dissocPath(path, oldRoute), R.dissocPath(path, newRoute))
      ) {
        console.debug('highlight cleared in search panel')
      } else {
        this.closeSidebar()
        this.trackPageview()
        this.trackEvent()
      }
    },

    isMobile: {
      handler: function () {
        // If device dimensions change sufficiently that we're
        // toggling between detecting mobile versus desktop, then
        // apply the defaults for the new detection. This allows
        // zoom buttons to appear and disappear, paging arrows to
        // appear and disappear, when resizing the window
        // drastically.
        this.prefs.setDefaults(this.isMobile ? 'mobile' : 'desktop')
        this.readMode = this.isMobile && this.readMode
      },
      immediate: true,
    },
  },

  created() {
    this.loadPages()

    api
      .getToc(this.tizraId)
      .then(r => {
        this.contents = r
      })
      .catch(() => {
        /* hide TOC */
      })

    // GA sends the initial trackPageview by default, so we only need
    // trackEvent on load.
    this.trackEvent()
  },

  mounted() {
    // Note the nomenclature:
    // imperative tense ("go") for commands, past tense ("searched")
    // for events.
    this.$sub('go', v => {
      this.navToPage(v)
    })
    this.$sub('toggleReadMode', () => {
      this.readMode = this.isMobile && !this.readMode
    })
    this.$sub('zoomed', z => {
      this.fetchPage(this.pageNum, z)
    })

    // Capture keypresses so we can change focus to the viewer for scrolling
    // keys.
    document.addEventListener('keydown', this.keydown, {capture: true})

    // Notice when the size of the nav changes, and adjust the size of the
    // toolbar buttons to fit. This is especially for accessibility while
    // zooming, to make sure the buttons don't fall off the bottom. (We used to
    // tie this to the height prop, but that only works on Chrome. On Firefox
    // the apparent height doesn't change when zooming, and the resize event
    // doesn't fire either.)
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(
        () => void this.adjustSidebarUnzoom(),
      )
      resizeObserver.observe(this.$refs.sidebar)
    }
    this.adjustSidebarUnzoom() // Initial adjustment
  },

  methods: {
    loadPages() {
      for (
        let p = this.pageNum - config.prefetchBackward;
        p <= this.pageNum + config.prefetchForward;
        p++
      ) {
        if (p >= this.firstPage && p <= this.lastPage) {
          this.fetchPage(p)
        }
      }
    },

    fetchPage(pageNum, zoom = 1) {
      // Choose a requestWidth from our list that's equal or larger
      // than needed for this device at current zoom.
      const nativeWidth = this.width * (window.devicePixelRatio || 1)
      const preferredWidth = nativeWidth * zoom
      const requestWidth =
        config.requestWidths.filter(w => w >= preferredWidth)[0] ||
        config.requestWidths[config.requestWidths.length - 1]

      if (this.pages[pageNum - 1].fetched) {
        const page = this.pages[pageNum - 1]
        if (page.requestWidth >= requestWidth) {
          // Already have a page with sufficient pixel dimensions for
          // the current zoom.
        } else if (page.imageUrl) {
          // Fetch a larger image to satisfy the current zoom.
          this.setPage(pageNum, {
            imageSrc: `${page.imageUrl}?width=${requestWidth}`,
            requestWidth,
          })
        }
        // At a glance, it would seem possible to repeatedly increment
        // or decrement `zoom` ineffectively, if imageUrl isn't known
        // yet. Then zoom and requestWidth would be out of sync. In fact
        // this can't happen, because the zoomIn/zoomOut methods call
        // down the component tree to the Zoomable component, which
        // doesn't render until imageUrl is available, so `zoom` won't
        // be modified either.
        return
      }

      // We've never fetched this page before.
      this.setPage(pageNum, {
        fetched: true,
        isFetching: true,
        requestWidth,
      })

      api
        .getPageInfo(this.tizraId, pageNum)
        .then(pageInfo => {
          this.setPage(pageNum, {
            ...pageInfo,
            isFetching: false,
            ...(pageInfo.imageUrl && {
              imageSrc: `${pageInfo.imageUrl}?width=${requestWidth}`,
            }),
          })
        })
        .catch(e => {
          // This might not be 403, instead it might be
          // a cross-origin redirect (EZproxy session timeout) or
          // another kind of error. But treating it as denied
          // allows Loader to handle it.
          this.setPage(pageNum, {
            denied: {e},
          })
        })
    },

    /**
     * Reactively update a page object in the pages array. The Reader
     * component owns the pages array and provides it as a prop to
     * subcomponents. This method is provided to subcomponents to
     * update individual pages, so that they will see the update in
     * their own props.
     *
     * @param {number} pageNum - physical 1-based page number
     * @param {Object} obj - sparse properties to update in the page
     */
    setPage(pageNum, obj) {
      this.$set(this.pages, pageNum - 1, {
        ...this.pages[pageNum - 1],
        ...obj,
      })
    },

    navToPage(v) {
      v = Math.max(this.firstPage, Math.min(this.lastPage, v))
      this.setPageNum(v)
      this.closeSidebar()
    },

    keydown(event) {
      if (this.sidebarOpen()) {
        if (event.key === 'Escape') {
          this.closeSidebar()
        }
        return
      }

      const {activeElement} = document
      if (activeElement) {
        if (activeElement.matches('input, select, textarea')) {
          return
        }
        if (event.key === ' ' && activeElement.matches('button')) {
          return
        }
        if (
          !['+', '-', '1'].includes(event.key) &&
          activeElement.matches('.t-viewer, .t-viewer *')
        ) {
          return
        }
      }

      // Don't handle keys when modifiers are pressed. For example, this avoids
      // handling browser zoom-out (ctrl-minus) from being additionally handled
      // by our code.
      const key = [
        event.altKey && 'Alt',
        event.ctrlKey && 'Ctrl',
        event.metaKey && 'Meta',
        event.shiftKey && 'Shift',
      ]
        .filter(Boolean)
        .concat(event.key)
        .join('')

      const {viewer} = this.$refs
      switch (key) {
        case '+':
        case 'Shift+':
          this.zoomIn()
          break
        case '-':
          this.zoomOut()
          break
        case '1':
          this.setZoom(1)
          break
        case ' ':
        case 'ArrowUp':
        case 'ArrowLeft':
        case 'ArrowDown':
        case 'ArrowRight':
        case 'PageUp':
        case 'PageDown':
        case 'Home':
        case 'End':
          // Move focus to the viewer to handle scrolling keys.
          viewer.$el.focus()
          // If the viewer has its own handler, pass on the event.
          if (viewer.keydown) {
            viewer.keydown(event)
          }
          break
      }
    },

    pagePrevious() {
      if (this.pageNum > this.firstPage) {
        this.$refs.viewer.setZoom(1, this.pageNum - 1)
        this.$refs.viewer.panTo({x: 0, y: 0, smooth: false}, this.pageNum - 1)
        this.setPageNum(this.pageNum - 1)
      }
    },

    pageNext() {
      if (this.pageNum < this.lastPage) {
        this.$refs.viewer.setZoom(1, this.pageNum + 1)
        this.$refs.viewer.panTo({x: 0, y: 0, smooth: false}, this.pageNum + 1)
        this.setPageNum(this.pageNum + 1)
      }
    },

    sidebarOpen(s) {
      return s === undefined ? !!this.sidebar : this.sidebar === s
    },

    closeSidebar() {
      if (this.sidebar) {
        this.sidebar = null

        // For most operations, when the sidebar closes, move focus to
        // the viewer to handle scrolling events. This ensures
        // document.activeElement isn't buried in display:none.
        this.$refs.viewer.$el.focus()
      }
    },

    toggleSidebar(s) {
      // Don't call closeSidebar or focus the viewer, because focus should
      // stay on the toggle button that was clicked.
      this.sidebar = this.sidebar === s ? null : s
    },

    zoomIn() {
      this.$refs.viewer.zoomIn()
    },

    zoomOut() {
      this.$refs.viewer.zoomOut()
    },

    setZoom(zoom) {
      this.$refs.viewer.setZoom(zoom)
    },

    trackPageview() {
      try {
        window.tizra.trackPageview()
      } catch (e) {
        console.error(e)
      }
    },

    trackEvent() {
      const {tizra} = window
      try {
        tizra.trackEvent(
          `view/page/${tizra.user.username}/${this.urlId}/${this.pageNum}`,
        )
      } catch (e) {
        console.error(e)
      }
    },

    tapPrevious(event) {
      if (event.pointerType === 'touch') {
        this.pagePrevious()
      }
    },

    tapNext(event) {
      if (event.pointerType === 'touch') {
        this.pageNext()
      }
    },

    /**
     * Keep the toolbar from overflowing off the bottom when the browser is
     * zoomed. This sets a CSS var that we use to unzoom the toolbar.
     */
    adjustSidebarUnzoom() {
      const sidebar = this.$refs.sidebar
      const toolbar = this.$refs.toolbar?.$el
      if (sidebar && toolbar) {
        const sidebarUnzoom =
          (this.sidebarUnzoom * sidebar.offsetHeight) / toolbar.scrollHeight
        this.sidebarUnzoom = Math.min(1, sidebarUnzoom)
      }
    },
  },

  provide() {
    return {
      isMobile: this.isMobile,
      logicalPages: this.logicalPages,
      pageNext: this.pageNext,
      pagePrevious: this.pagePrevious,
      pubInfo: this.pubInfo,
      setPage: this.setPage,
      setPageNum: this.setPageNum,
    }
  },

  inject: ['settings'],

  components: {
    AttachmentsPanel,
    ButtonLink,
    ButtonPush,
    ButtonToggle,
    Footer,
    IconLink,
    Panel,
    SearchPanel,
    Strip,
    Toc,
    Toolbar,
    Tower,
  },
}
</script>
